import isNil from 'lodash-es/isNil'
import sumBy from 'lodash-es/sumBy'
import React, { useState } from 'react'

import { Button, ModalBox, Option, ProductsList, useModalContext } from '@fourthwall/components'
import type { OrderItemsReplacements } from '@fourthwall/services/dist/OrderApiClient'
import type { Offer } from '@fourthwall/services/dist/models/offer'
import type { OrderOffer } from '@fourthwall/services/dist/models/order'

import * as S from './OrderVariantsModal.styled'

export type OrderVariantsModalProps = {
  offer: OrderOffer
  replacements: OrderItemsReplacements[]
  onConfirm: (offer: OrderOffer) => void
}

export const OrderVariantsModal = ({ offer, replacements, onConfirm }: OrderVariantsModalProps) => {
  const { close } = useModalContext()

  const variantTypes = offer.offer.variantTypes
  const [selectedVariantId, setSelectedVariantId] = useState(offer.variant.id)
  const hasVariants = !!variantTypes?.length

  const variants = offer.offer.variants
    .filter((variant) => variant.id === selectedVariantId || variant.status !== 'ARCHIVED')
    .map((variant) => {
      const isReplacingFrom = (replacement: any) => replacement.from === variant.id
      const isReplacingTo = (replacement: any) => replacement.to === variant.id

      const additionalQuantity = sumBy(replacements.filter(isReplacingFrom), 'quantity')
      const selectedQuantity = sumBy(replacements.filter(isReplacingTo), 'quantity')
      const currentQuantity = !isNil(variant.quantity)
        ? variant.quantity + additionalQuantity - selectedQuantity
        : variant.quantity

      return {
        id: variant.id,
        name: variant.name,
        description: variant.attributesDescription,
        quantity: variant.quantity,
        disabled: !isNil(currentQuantity) && currentQuantity < offer.quantity,
        selected: selectedVariantId === variant.id,
      }
    })

  const selectedVariant = offer.offer.variants?.find((variant) => variant.id === selectedVariantId)
  const currentVariant = {
    id: offer.variant.id,
    name: offer.variant.name,
    description: offer.variant.attributesDescription,
    offerType: offer.offer.offerType,
    // @ts-ignore
    image: offer.offer.thumbnailImage,
  }
  const disabled = !hasVariants || !selectedVariant

  const handleConfirm = () => {
    close()

    if (!selectedVariant) {
      return
    }

    onConfirm({
      ...offer,
      price: selectedVariant.price,
      variantId: selectedVariant.id,
      variant: {
        ...selectedVariant,
        images: selectedVariant.images.map((imageId) =>
          offer.offer.images.find((offerImage) => offerImage.id === imageId),
        ) as Offer.Image[],
      },
    })
  }

  const renderVariants = () => {
    if (!hasVariants) {
      return (
        <S.EmptyVariantsNotice>
          This offer doesn't have set up variants that can use as a replacement.
        </S.EmptyVariantsNotice>
      )
    }

    return (
      <S.VariantsContainer>
        {variants?.map((variant) => (
          <S.VariantContainer key={variant.id}>
            <Option
              checked={variant.selected}
              disabled={variant.disabled}
              label={
                <S.OptionLabel disabled={Boolean(variant.disabled)}>
                  <div>{variant.description}</div>
                  <div>
                    {isNil(variant.quantity) ? 'Unlimited' : `${variant.quantity} in stock`}
                  </div>
                </S.OptionLabel>
              }
              onChange={() => setSelectedVariantId(variant.id)}
            />
          </S.VariantContainer>
        ))}
      </S.VariantsContainer>
    )
  }

  return (
    <ModalBox
      title="Edit variant"
      buttons={[
        <Button label="Cancel" appearance="secondary" onClick={close} />,
        <Button label="Update" appearance="primary" disabled={disabled} onClick={handleConfirm} />,
      ]}
      onClose={close}
    >
      <S.ProductSummaryContainer>
        <ProductsList>
          <ProductsList.Item
            name={currentVariant.name}
            description={currentVariant.description}
            image={currentVariant.image}
            type={currentVariant.offerType === 'GIFT_CARD' ? 'giftCard' : undefined}
          />
        </ProductsList>
      </S.ProductSummaryContainer>
      {renderVariants()}
    </ModalBox>
  )
}
